// ============================================================================
// REPORT SERVICE - Production Ready with Cloud Functions
// ============================================================================
// Features:
// - Submits reports via Cloud Function (rate limited, validated server-side)
// - Admin actions via Cloud Functions (proper auth, audit logging)
// - Real-time subscription for admin dashboard
// - Proper error handling
// ============================================================================

import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

// ============================================================================
// TYPES
// ============================================================================

export type ReportReason = 
  | 'offensive_content'
  | 'spam'
  | 'misinformation'
  | 'privacy_violation'
  | 'betting_manipulation'
  | 'harassment'
  | 'cheating'
  | 'other';

export type ReportStatus = 'pending' | 'under_review' | 'action_taken' | 'dismissed';

export type ReportAction = 
  | 'none'
  | 'warning_issued'
  | 'temporary_ban'
  | 'permanent_ban';

export interface UserReport {
  id: string;
  // Who was reported
  reportedUserId: string;
  reportedUsername: string;
  reportedPhotoURL?: string;
  // Who submitted the report
  reporterUserId: string;
  reporterUsername: string;
  // Report details
  reason: ReportReason;
  reasonLabel: string;
  details?: string;
  evidenceUrls?: string[];
  // Status & resolution
  status: ReportStatus;
  action?: ReportAction;
  actionDetails?: string;
  adminNotes?: string;
  reviewedBy?: string;
  reviewedByUsername?: string;
  reviewedAt?: Timestamp;
  // Timestamps
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

export interface ReportStats {
  totalReports: number;
  pendingReports: number;
  thisWeekReports: number;
  thisMonthReports: number;
  pendingAppeals?: number;
}

export interface BanAppeal {
  id: string;
  userId: string;
  username: string;
  email?: string;
  banReason: string;
  banType: string;
  bannedAt?: Timestamp;
  appealMessage: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy?: string;
  reviewedAt?: Timestamp;
  reviewNotes?: string;
  createdAt: Timestamp;
}

// ============================================================================
// REASON MAPPING
// ============================================================================

export const REPORT_REASONS: { value: ReportReason; label: string; icon: string }[] = [
  { value: 'offensive_content', label: 'Offensive or Inappropriate Content', icon: '' },

  { value: 'spam', label: 'Spam and Unwanted Advertising', icon: '' },

  { value: 'misinformation', label: 'Misinformation', icon: '' },

  { value: 'privacy_violation', label: 'Violation of Safety and Privacy Rules', icon: '' },

  { value: 'betting_manipulation', label: 'False or Manipulative Betting Information', icon: '' },

  { value: 'harassment', label: 'Uncivil Behavior or Harassment', icon: '' },

  { value: 'cheating', label: 'Suspected Cheating or Collusion', icon: '🃏' },
  { value: 'other', label: 'Other', icon: '' },

];

export const getReasonLabel = (reason: ReportReason): string => {
  return REPORT_REASONS.find(r => r.value === reason)?.label || reason;
};

export const getReasonIcon = (reason: ReportReason): string => {
  return REPORT_REASONS.find(r => r.value === reason)?.icon || '';

};

export const STATUS_LABELS: Record<ReportStatus, { label: string; color: string }> = {
  pending: { label: 'Pending Review', color: 'yellow' },
  under_review: { label: 'Under Review', color: 'blue' },
  action_taken: { label: 'Action Taken', color: 'green' },
  dismissed: { label: 'Dismissed', color: 'gray' },
};

export const ACTION_LABELS: Record<ReportAction, { label: string; color: string }> = {
  none: { label: 'No Action', color: 'gray' },
  warning_issued: { label: 'Warning Issued', color: 'yellow' },
  temporary_ban: { label: 'Temporary Ban', color: 'orange' },
  permanent_ban: { label: 'Permanent Ban', color: 'red' },
};

// ============================================================================
// CLOUD FUNCTION CALLS
// ============================================================================

/**
 * Submit a report via Cloud Function (rate limited, validated server-side)
 */
export const submitReport = async (
  reportedUserId: string,
  reason: ReportReason,
  details?: string,
  evidenceUrls?: string[]
): Promise<{ success: boolean; reportId?: string; message: string }> => {
  try {
    const submitUserReport = httpsCallable<
      { reportedUserId: string; reason: string; details?: string; evidenceUrls?: string[] },
      { success: boolean; reportId?: string; message: string }
    >(functions, 'submitUserReport');
    
    const result = await submitUserReport({
      reportedUserId,
      reason,
      details,
      evidenceUrls: evidenceUrls && evidenceUrls.length ? evidenceUrls.slice(0, 3) : undefined,
    });
    
    return result.data;
  } catch (error: any) {
    console.error('Error submitting report:', error);
    
    // Extract error message from Cloud Function error
    const message = error.message || 'Failed to submit report';
    
    // Handle specific error codes
    if (error.code === 'functions/resource-exhausted') {
      throw new Error('You have reached the maximum number of reports. Please try again later.');
    }
    if (error.code === 'functions/already-exists') {
      throw new Error('You have already reported this user. Please wait for review.');
    }
    
    throw new Error(message);
  }
};

/**
 * Admin: Take action on a report
 */
export const takeReportAction = async (
  reportId: string,
  action: 'dismiss' | 'warning' | 'temporary_ban' | 'permanent_ban',
  actionNotes?: string,
  banDurationHours?: number
): Promise<{ success: boolean; message: string }> => {
  try {
    const adminReportAction = httpsCallable<
      { reportId: string; action: string; actionNotes?: string; banDurationHours?: number },
      { success: boolean; action: string; message: string }
    >(functions, 'adminReportAction');
    
    const result = await adminReportAction({
      reportId,
      action,
      actionNotes,
      banDurationHours,
    });
    
    return result.data;
  } catch (error: any) {
    console.error('Error taking report action:', error);
    throw new Error(error.message || 'Failed to process action');
  }
};

/**
 * Admin: Mark report as under review
 */
export const markReportUnderReview = async (reportId: string): Promise<void> => {
  try {
    const adminMarkReportUnderReview = httpsCallable<
      { reportId: string },
      { success: boolean }
    >(functions, 'adminMarkReportUnderReview');
    
    await adminMarkReportUnderReview({ reportId });
  } catch (error: any) {
    console.error('Error marking report under review:', error);
    throw new Error(error.message || 'Failed to update report');
  }
};

/**
 * Admin: Delete a report
 */
export const deleteReport = async (reportId: string): Promise<void> => {
  try {
    const adminDeleteReport = httpsCallable<
      { reportId: string },
      { success: boolean }
    >(functions, 'adminDeleteReport');
    
    await adminDeleteReport({ reportId });
  } catch (error: any) {
    console.error('Error deleting report:', error);
    throw new Error(error.message || 'Failed to delete report');
  }
};

/**
 * Admin: Unban a user
 */
export const unbanUser = async (userId: string, reason?: string): Promise<void> => {
  try {
    const adminUnbanUser = httpsCallable<
      { userId: string; reason?: string },
      { success: boolean }
    >(functions, 'adminUnbanUser');
    
    await adminUnbanUser({ userId, reason });
  } catch (error: any) {
    console.error('Error unbanning user:', error);
    throw new Error(error.message || 'Failed to unban user');
  }
};

/**
 * User: Appeal a ban
 */
export const appealBan = async (appealMessage: string): Promise<{ success: boolean; appealId?: string; message: string }> => {
  try {
    const appealBanFn = httpsCallable<
      { appealMessage: string },
      { success: boolean; appealId?: string; message: string }
    >(functions, 'appealBan');
    
    const result = await appealBanFn({ appealMessage });
    return result.data;
  } catch (error: any) {
    console.error('Error submitting appeal:', error);
    throw new Error(error.message || 'Failed to submit appeal');
  }
};

// ============================================================================
// FIRESTORE READS (for admin dashboard display)
// ============================================================================

/**
 * Get reports (admin only - for display)
 */
export const getReports = async (
  statusFilter?: ReportStatus,
  maxResults: number = 50
): Promise<UserReport[]> => {
  try {
    let q;
    
    if (statusFilter) {
      q = query(
        collection(db, 'reports'),
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );
    } else {
      q = query(
        collection(db, 'reports'),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );
    }

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as UserReport));
  } catch (error) {
    console.error('Error getting reports:', error);
    return [];
  }
};

/**
 * Subscribe to reports in real-time
 */
export const subscribeToReports = (
  onUpdate: (reports: UserReport[]) => void,
  statusFilter?: ReportStatus,
  maxResults: number = 50
): (() => void) => {
  let q;
  
  if (statusFilter) {
    q = query(
      collection(db, 'reports'),
      where('status', '==', statusFilter),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );
  } else {
    q = query(
      collection(db, 'reports'),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );
  }

  return onSnapshot(q, (snapshot) => {
    const reports = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as UserReport));
    onUpdate(reports);
  }, (error) => {
    console.error('Error subscribing to reports:', error);
    onUpdate([]);
  });
};

/**
 * Get report stats via Cloud Function
 */
export const getReportStats = async (): Promise<ReportStats> => {
  try {
    const getReportStatsFn = httpsCallable<
      Record<string, never>,
      ReportStats
    >(functions, 'getReportStats');
    
    const result = await getReportStatsFn({});
    return result.data;
  } catch (error) {
    console.error('Error getting report stats:', error);
    // Fallback to local calculation
    return getReportStatsLocal();
  }
};

/**
 * Local fallback for report stats (in case Cloud Function fails)
 */
const getReportStatsLocal = async (): Promise<ReportStats> => {
  try {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const allReportsQuery = query(
      collection(db, 'reports'),
      orderBy('createdAt', 'desc'),
      limit(500)
    );
    const allSnapshot = await getDocs(allReportsQuery);

    let totalReports = 0;
    let pendingReports = 0;
    let thisWeekReports = 0;
    let thisMonthReports = 0;

    allSnapshot.docs.forEach(doc => {
      const data = doc.data();
      totalReports++;
      
      if (data.status === 'pending') {
        pendingReports++;
      }

      const createdAt = data.createdAt?.toDate();
      if (createdAt) {
        if (createdAt >= weekAgo) {
          thisWeekReports++;
        }
        if (createdAt >= monthAgo) {
          thisMonthReports++;
        }
      }
    });

    return {
      totalReports,
      pendingReports,
      thisWeekReports,
      thisMonthReports,
    };
  } catch (error) {
    console.error('Error getting report stats locally:', error);
    return {
      totalReports: 0,
      pendingReports: 0,
      thisWeekReports: 0,
      thisMonthReports: 0,
    };
  }
};

/**
 * Get reports against a specific user
 */
export const getReportsAgainstUser = async (userId: string): Promise<UserReport[]> => {
  try {
    const q = query(
      collection(db, 'reports'),
      where('reportedUserId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as UserReport));
  } catch (error) {
    console.error('Error getting reports against user:', error);
    return [];
  }
};

/**
 * Get ban appeals (admin only)
 */
export const getBanAppeals = async (
  statusFilter?: 'pending' | 'approved' | 'rejected',
  maxResults: number = 50
): Promise<BanAppeal[]> => {
  try {
    let q;
    
    if (statusFilter) {
      q = query(
        collection(db, 'banAppeals'),
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );
    } else {
      q = query(
        collection(db, 'banAppeals'),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );
    }

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as BanAppeal));
  } catch (error) {
    console.error('Error getting ban appeals:', error);
    return [];
  }
};

/**
 * Admin: Handle appeal (approve = unban, reject = keep banned)
 */
export const handleAppeal = async (
  appealId: string,
  action: 'approve' | 'reject',
  reviewNotes?: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const adminHandleAppeal = httpsCallable<
      { appealId: string; action: 'approve' | 'reject'; reviewNotes?: string },
      { success: boolean; action: string; message: string }
    >(functions, 'adminHandleAppeal');
    
    const result = await adminHandleAppeal({
      appealId,
      action,
      reviewNotes,
    });
    
    return result.data;
  } catch (error: any) {
    console.error('Error handling appeal:', error);
    throw new Error(error.message || 'Failed to process appeal');
  }
};

/**
 * Get banned users (admin only)
 */
export interface BannedUser {
  id: string;
  username: string;
  email?: string;
  photoURL?: string;
  banned: boolean;
  banType: 'temporary' | 'permanent';
  banReason: string;
  bannedAt?: Timestamp;
  banExpiresAt?: Timestamp;
  warningsReceived?: number;
  bansReceived?: number;
}

export const getBannedUsers = async (maxResults: number = 100): Promise<BannedUser[]> => {
  try {
    const q = query(
      collection(db, 'users'),
      where('banned', '==', true),
      limit(maxResults)
    );

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      username: doc.data().username || 'Unknown',
      email: doc.data().email,
      photoURL: doc.data().photoURL,
      banned: doc.data().banned,
      banType: doc.data().banType || 'permanent',
      banReason: doc.data().banReason || 'Unknown',
      bannedAt: doc.data().bannedAt,
      banExpiresAt: doc.data().banExpiresAt,
      warningsReceived: doc.data().warningsReceived || 0,
      bansReceived: doc.data().bansReceived || 0,
    } as BannedUser));
  } catch (error) {
    console.error('Error getting banned users:', error);
    return [];
  }
};

/**
 * Get admin audit log (admin only)
 */
export interface AuditLogEntry {
  id: string;
  adminId: string;
  adminUsername: string;
  action: string;
  targetUserId: string;
  targetUsername: string;
  details: Record<string, any>;
  timestamp: Timestamp;
}

export const getAuditLog = async (
  actionFilter?: string,
  maxResults: number = 100
): Promise<AuditLogEntry[]> => {
  try {
    let q;
    
    if (actionFilter) {
      q = query(
        collection(db, 'adminAuditLog'),
        where('action', '==', actionFilter),
        orderBy('timestamp', 'desc'),
        limit(maxResults)
      );
    } else {
      q = query(
        collection(db, 'adminAuditLog'),
        orderBy('timestamp', 'desc'),
        limit(maxResults)
      );
    }

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as AuditLogEntry));
  } catch (error) {
    console.error('Error getting audit log:', error);
    return [];
  }
};

// ============================================================================
// LEGACY COMPATIBILITY - These call Cloud Functions now
// ============================================================================

// Kept for backwards compatibility with existing code
export const updateReportStatus = async (
  reportId: string,
  status: ReportStatus,
  _adminUserId: string,
  _adminUsername: string,
  _adminNotes?: string
): Promise<void> => {
  if (status === 'under_review') {
    await markReportUnderReview(reportId);
  }
  // Other status updates happen through takeReportAction
};

export const dismissReport = async (
  reportId: string,
  _adminUserId: string,
  _adminUsername: string,
  dismissReason?: string
): Promise<void> => {
  await takeReportAction(reportId, 'dismiss', dismissReason);
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

const reportService = {
  // Cloud Function calls
  submitReport,
  takeReportAction,
  markReportUnderReview,
  deleteReport,
  unbanUser,
  appealBan,
  handleAppeal,
  
  // Firestore reads
  getReports,
  subscribeToReports,
  getReportStats,
  getReportsAgainstUser,
  getBanAppeals,
  getBannedUsers,
  getAuditLog,
  
  // Legacy compatibility
  updateReportStatus,
  dismissReport,
  
  // Constants
  REPORT_REASONS,
  getReasonLabel,
  getReasonIcon,
  STATUS_LABELS,
  ACTION_LABELS,
};

export default reportService;